import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../auth";
import { TripCtx } from "./TripLayout";
import { Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function ConciergePage() {
  const { tripId } = useParams();
  const { API, authHeaders, user } = useAuth();
  const { trip } = useContext(TripCtx);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [waNumber, setWaNumber] = useState("");
  const [othersTyping, setOthersTyping] = useState([]);
  const scrollRef = useRef(null);
  const typingTimeout = useRef(null);
  const lastTypingSent = useRef(0);

  const load = () => axios.get(`${API}/trips/${tripId}/messages`, { headers: authHeaders }).then((r) => setMsgs(r.data));
  const loadTyping = () => axios.get(`${API}/trips/${tripId}/typing`, { headers: authHeaders })
    .then((r) => setOthersTyping(r.data.typing || [])).catch(() => {});

  useEffect(() => {
    load();
    loadTyping();
    axios.get(`${API}/config/concierge`, { headers: authHeaders }).then((r) => setWaNumber(r.data.whatsapp_number || ""));
    const t = setInterval(load, 8000);
    const tt = setInterval(loadTyping, 3000);
    return () => { clearInterval(t); clearInterval(tt); };
  }, [tripId]);

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length, othersTyping.length]);

  const notifyTyping = () => {
    const now = Date.now();
    if (now - lastTypingSent.current > 4000) {
      lastTypingSent.current = now;
      axios.post(`${API}/trips/${tripId}/typing`, { is_typing: true }, { headers: authHeaders }).catch(() => {});
    }
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      axios.post(`${API}/trips/${tripId}/typing`, { is_typing: false }, { headers: authHeaders }).catch(() => {});
      lastTypingSent.current = 0;
    }, 5000);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/trips/${tripId}/messages`, { body }, { headers: authHeaders });
      setMsgs([...msgs, data]);
      setBody("");
      axios.post(`${API}/trips/${tripId}/typing`, { is_typing: false }, { headers: authHeaders }).catch(() => {});
    } catch { toast.error("Send failed"); } finally { setBusy(false); }
  };

  const waLink = waNumber ? `https://wa.me/${waNumber.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi from ${trip.destination} trip`)}` : null;
  const typerLabel = othersTyping.length > 0
    ? `${othersTyping[0].role === "lead_client" ? othersTyping[0].name.split(" ")[0] : othersTyping[0].name} is typing…`
    : null;

  return (
    <div data-testid="concierge-page" className="py-4 flex flex-col" style={{ minHeight: "70vh" }}>
      <div className="flex justify-between items-end mb-4">
        <div>
          <p className="tcm-eyebrow">{trip.destination}</p>
          <h1 className="font-display text-4xl mt-1">Concierge</h1>
        </div>
        {waLink && (
          <a data-testid="wa-continue" href={waLink} target="_blank" rel="noreferrer"
             className="tcm-btn-outline inline-flex items-center gap-2">
            <ExternalLink size={12} /> WhatsApp
          </a>
        )}
      </div>
      <hr className="tcm-gold-rule w-16 mb-4" />

      <div className="flex-1 space-y-4 pb-6" data-testid="messages-list">
        {msgs.length === 0 && (
          <p className="text-sm text-[color:var(--tcm-navy-70)] py-6 text-center">
            Say hello — your concierge is standing by.
          </p>
        )}
        {msgs.map((m, idx) => {
          const mine = m.sender_id === user?.id;
          const staff = m.sender_role !== "lead_client";
          const isLeadClient = user?.role === "lead_client";
          // For own messages, check whether the OTHER side has read it
          const seenByOther = mine && (isLeadClient ? m.read_by_staff : m.read_by_client);
          const seenAt = mine && (isLeadClient ? m.read_at_staff : m.read_at_client);
          const isLastMine = mine && msgs.slice(idx + 1).every((x) => x.sender_id !== user?.id);
          return (
            <div key={m.id} data-testid={`msg-${m.id}`}
                 className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              <p className="tcm-eyebrow mb-1" style={{color: staff && !mine ? "var(--tcm-gold-700)" : ""}}>
                {mine ? "You" : m.sender_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </p>
              <div className="border border-[#1E2D50] p-3 max-w-[80%]"
                   style={{ background: mine ? "var(--tcm-navy)" : "transparent", color: mine ? "var(--tcm-cream)" : "var(--tcm-navy)" }}>
                <p className="whitespace-pre-wrap text-sm">{m.body}</p>
              </div>
              {mine && isLastMine && (
                <p className="tcm-eyebrow mt-1 text-[10px]" data-testid={`receipt-${m.id}`}
                   style={{ color: seenByOther ? "var(--tcm-gold-700)" : "var(--tcm-navy-70)" }}>
                  {seenByOther
                    ? `Seen${seenAt ? " · " + new Date(seenAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}`
                    : "Delivered"}
                </p>
              )}
            </div>
          );
        })}
        {typerLabel && (
          <div data-testid="typing-indicator" className="flex items-center gap-2 text-[12px] text-[color:var(--tcm-navy-70)] italic pl-1">
            <span className="inline-flex gap-0.5">
              <span className="tcm-typing-dot" />
              <span className="tcm-typing-dot" style={{ animationDelay: "150ms" }} />
              <span className="tcm-typing-dot" style={{ animationDelay: "300ms" }} />
            </span>
            {typerLabel}
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      <form onSubmit={send} className="sticky bottom-24 bg-[#F5EFE6] pt-3 border-t border-[color:var(--tcm-navy-15)]">
        <div className="flex gap-2">
          <input data-testid="msg-input" className="tcm-input flex-1" placeholder="Type a message..."
                 value={body}
                 onChange={(e) => { setBody(e.target.value); notifyTyping(); }} />
          <button data-testid="msg-send" disabled={busy || !body.trim()} className="tcm-btn-primary px-4">
            <Send size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}
